import { createTRPCReact, getQueryKey } from '@trpc/react-query'
import { httpBatchLink } from '@trpc/client'
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import type { QueryKey } from '@tanstack/react-query'
import superjson, { deserialize as superjsonDeserialize } from 'superjson'
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server'
import type { AppRouter } from '@retail/backend'
import { makeRefreshFetch } from '@retail/refresh-fetch'

// Re-export for consumers who need the low-level types
export type { AppRouter } from '@retail/backend'
export type RouterInputs = inferRouterInputs<AppRouter>
export type RouterOutputs = inferRouterOutputs<AppRouter>

// The singleton tRPC React instance — wrap your app with:
//   <trpc.Provider client={trpcClient} queryClient={queryClient}>
//     <QueryClientProvider client={queryClient}>
//       {children}
//     </QueryClientProvider>
//   </trpc.Provider>
export const trpc = createTRPCReact<AppRouter>()

export type CreateTrpcClientOpts = {
  /**
   * Base URL of the tRPC endpoint.
   * Defaults to `VITE_API_URL + '/trpc'` if the env var is set, otherwise `'/trpc'`
   * (relative URL — resolved via the Vite dev proxy in development).
   */
  url?: string
}

/** The envelope superjson puts a value in on the wire — its own input type. */
type SuperjsonEnvelope = Parameters<typeof superjsonDeserialize>[0]

/**
 * Reads the payload out of a tRPC success body, letting superjson undo its own
 * wire format rather than indexing into `.json` by hand. The nesting is
 * superjson's business, not ours: reading the un-nested shape returns undefined
 * forever, and that failed silently once already: every reload bounced to
 * /login because the refresh looked unsuccessful.
 */
const readSuccessPayload = (body: unknown): Record<string, unknown> | null => {
  const envelope = (body as { result?: { data?: unknown } } | null | undefined)?.result?.data
  if (envelope === null || typeof envelope !== 'object') {
    return null
  }
  try {
    const value = superjsonDeserialize(envelope as SuperjsonEnvelope)
    return value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : null
  } catch {
    // A body that is not a superjson envelope is simply not a successful refresh.
    return null
  }
}

/**
 * Confirms a successful `auth.refresh` from its raw JSON body. Cookie mode never
 * reads the token value — the new tokens ride the httpOnly Set-Cookie headers —
 * but a valid body is the signal that the refresh actually issued them, so
 * `makeRefreshFetch` should retry. Kept as its own function so the exact wire
 * shape is pinned by a unit test (mirrors the sibling admin client's
 * `parseRefreshResponseBody`).
 */
export const isRefreshResponseOk = (body: unknown): boolean => {
  const accessToken = readSuccessPayload(body)?.accessToken
  return typeof accessToken === 'string' && accessToken.length > 0
}

/**
 * The two calls whose own 401 is the ANSWER, not a symptom of an expired
 * session — `makeRefreshFetch` hands their 401 straight back instead of
 * refreshing:
 *
 *   - `auth.refresh`, so a refresh issued through this client cannot recurse
 *     (dormant today: refreshFn posts with a bare fetch);
 *   - `auth.login`, which fires on every failed sign-in. Refreshing there spends
 *     a round-trip only to fail with no cookie to refresh, and its error
 *     REPLACES the login's — the copy on this screen is one fixed sentence
 *     either way, but the admin SPA showed "session expired" to someone who
 *     never had one, and the two clients must not drift on the same question.
 */
export const isSelfAnsweringAuthUrl = (url: RequestInfo | URL): boolean => {
  const str = typeof url === 'string' ? url : url instanceof URL ? url.href : String(url)
  return str.includes('auth.refresh') || str.includes('auth.login')
}

/**
 * Creates a tRPC client with:
 *   - httpBatchLink with `credentials: 'include'` (httpOnly cookie auth, no Authorization header)
 *   - Transparent token refresh: on 401, calls `auth.refresh` (cookie mode, no args), then retries
 *   - superjson transformer
 *
 * Usage:
 *   const queryClient = createQueryClient()
 *   const trpcClient = createTrpcClient()
 */
export const createTrpcClient = (opts: CreateTrpcClientOpts = {}) => {
  const baseUrl =
    opts.url ??
    (typeof import.meta !== 'undefined' && (import.meta as { env?: Record<string, string> }).env?.VITE_API_URL
      ? `${(import.meta as { env?: Record<string, string> }).env!.VITE_API_URL}/trpc`
      : '/trpc')

  // refreshFn is a plain fetch to the refresh mutation — no tRPC client dependency to avoid
  // circular references. Cookie mode: the server reads clientId/userId/refreshToken from the
  // httpOnly cookie, so the input is empty — but the router runs the superjson transformer,
  // which requires the empty input wrapped as `{ json: {} }`. Posting a bare `{}` deserialises
  // to `undefined` and zod rejects it (400), which used to turn every reload into a bounce to
  // /login. The rotated tokens come back only via Set-Cookie; the JSON body is
  // used solely to confirm the refresh succeeded.
  const refreshFn = async (): Promise<void> => {
    const res = await fetch(`${baseUrl}/auth.refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ json: {} }),
    })
    if (!res.ok || !isRefreshResponseOk(await res.json())) {
      throw new Error('UNAUTHORIZED')
    }
  }

  const customFetch = makeRefreshFetch(
    (url, init) => fetch(url, { ...init, credentials: 'include' }),
    refreshFn,
    { skipRefreshFor: isSelfAnsweringAuthUrl },
  )

  return trpc.createClient({
    links: [
      httpBatchLink({
        url: baseUrl,
        fetch: customFetch,
        transformer: superjson,
      }),
    ],
  })
}

/**
 * True when an error means "this session is over": either a tRPC UNAUTHORIZED,
 * or the bare `Error('UNAUTHORIZED')` that `makeRefreshFetch` throws when the
 * silent refresh itself failed (that one carries no tRPC `data` shape).
 */
export const isUnauthorizedError = (error: unknown): boolean => {
  if (error == null || typeof error !== 'object') {
    return false
  }
  const { data, message } = error as { data?: { code?: unknown }; message?: unknown }
  if (data?.code === 'UNAUTHORIZED') {
    return true
  }
  return typeof message === 'string' && message.includes('UNAUTHORIZED')
}

// `auth.me` is the query the route guard reads. getQueryKey (rather than a
// hand-written array) keeps this tied to the router: rename the procedure and
// this stops compiling instead of silently matching nothing.
const sessionQueryKey = getQueryKey(trpc.auth.me)

const isSessionQuery = (queryKey: QueryKey): boolean => {
  const [path] = queryKey
  const [sessionPath] = sessionQueryKey
  return Array.isArray(path) && Array.isArray(sessionPath) && path.join('.') === sessionPath.join('.')
}

/**
 * Creates a TanStack QueryClient with sensible defaults for tRPC usage.
 * Share a single instance between `trpc.Provider` and `QueryClientProvider`.
 *
 * Expired sessions are handled centrally here. A session can die between two
 * renders, and `auth.me` — the only query the route guard reads — is cached with
 * a staleTime, so nothing would notice: the screen would keep the stale user
 * while every other query surfaced an error. Instead, any query or mutation that
 * comes back UNAUTHORIZED invalidates `auth.me`; its refetch 401s in turn and
 * the existing guard lands the user on /login.
 */
export const createQueryClient = (): QueryClient => {
  const invalidateSessionOnExpiry = (error: unknown, queryKey?: QueryKey): void => {
    if (!isUnauthorizedError(error)) {
      return
    }
    // Skip `auth.me`'s own failure: it is already the signal the guard reads,
    // and invalidating it here would refetch → fail → invalidate, forever.
    if (queryKey !== undefined && isSessionQuery(queryKey)) {
      return
    }
    void queryClient.invalidateQueries({ queryKey: sessionQueryKey })
  }

  const queryClient: QueryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => invalidateSessionOnExpiry(error, query.queryKey),
    }),
    mutationCache: new MutationCache({
      onError: (error) => invalidateSessionOnExpiry(error),
    }),
    defaultOptions: {
      queries: {
        // Stale time: 30 s — avoids waterfall refetches on mount
        staleTime: 30_000,
        retry: false,
      },
    },
  })

  return queryClient
}
